import React from 'react';
import WavePlayer from '../../WavePlayer';
import './audioFileList.scss';
import { Button } from 'reactstrap';
// eslint-disable-next-line no-unused-vars
// import FlightingCodeInput from '../../utility/FileUploader/FlightingCodeInput';
import GenericConfirmModal from '../../GenericModal/ConfirmModal';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import VersionBadge from '../../VersionBadge/VersionBadge';

const AudioFileRow = React.memo(
  ({ data, index, group, deleteFile, flightingCodeChange, id, showRadio, selectedMp3Id, onSelectMp3Id, approvalStatus, qcStatus, currentQcVersion, maxQcForVersion }) => {
    const [showConfirm, setShowConfirm] = React.useState(false);
    const isOlderFileVersion = data.version && data.version < (data.currentFileVersion || data.version);
    const fileApprovalStatus = isOlderFileVersion ? 'rejected' : approvalStatus;

    // QC status per file: compare against max QC version for THIS file version
    const isOlderQcWithinVersion = data.qcVersion && data.qcVersion < (maxQcForVersion || 1);
    let fileQcStatus;
    if (isOlderFileVersion) {
      // Older file version: the latest QC for that version passed (version was bumped due to rejection, not QC fail)
      // Earlier QC runs within that version failed (superseded by QC fix)
      fileQcStatus = isOlderQcWithinVersion ? 'fail' : 'pass';
    } else {
      // Current file version: use track's live QC status for the latest QC, fail for older QC runs
      fileQcStatus = isOlderQcWithinVersion ? 'fail' : qcStatus;
    }

    const showQcBadge = data.qcVersion >= 1 && (isOlderQcWithinVersion || isOlderFileVersion || fileQcStatus === 'pass' || fileQcStatus === 'fail' || fileQcStatus === 'testing');

    return (
      <>
        <div className="audio-file-list-row d-flex align-items-center flex-row pl-2 pr-2 pt-2 pb-2">
          <div className="flex-grow-1 player">
            {data.downloadUrl && (
              <WavePlayer key={`${id}-${index}`} file={data.downloadUrl} id={`${id}-${index}`} />
            )}
          </div>
          <div className="pl-2 pr-2" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', flexShrink: 0 }}>
            <span>{data.fileName}</span>
            {data.version && (
              <VersionBadge type="version" version={data.version} status={fileApprovalStatus} />
            )}
            {showQcBadge && (
              <VersionBadge type="qc" version={data.qcVersion} status={fileQcStatus} />
            )}
          </div>
          <div className="d-flex align-items-center justify-content-end" style={{height: '100%', minWidth: 'auto'}}>
            {/* Default MP3 checkmark (styled like playlist) */}
            {showRadio && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Checkbox
                  checked={selectedMp3Id === data.storageRef}
                  onChange={() => onSelectMp3Id(data.storageRef)}
                  icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                  checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                  style={{ marginRight: 0, padding: 0, height: 28, width: 28 }}
                  inputProps={{ title: 'Set as default MP3 for preview' }}
                />
              </div>
            )}
            <ModalConsumer>
              {({ showModal }) => (
                <>
                  <Button
                    className="btn-round btn-simple p-1 m-0"
                    style={{ border: 'none', minWidth: 0, width: 28, height: 28, background: 'transparent', backgroundColor: 'transparent' }}
                    onClick={() => setShowConfirm(true)}
                  >
                    <i className="tim-icons icon-trash-simple" style={{ color: '#fff', fontSize: 18 }}></i>
                  </Button>
                  {showConfirm && (
                    <GenericConfirmModal
                      isOpen={true}
                      title="Delete Audio File"
                      body={`Are you sure you want to delete ${data.fileName}? This action cannot be undone.`}
                      callback={() => {
                        setShowConfirm(false);
                        deleteFile(data.storageRef, index, group);
                      }}
                      cancelCallback={() => setShowConfirm(false)}
                    />
                  )}
                </>
              )}
            </ModalConsumer>
          </div>
        </div>
        <div className="d-flex flex-column pt-2">
          {/* <FlightingCodeInput
            flightingCode={data.flightingCode}
            id={id}
            onChange={(event) => {
              flightingCodeChange({ code: event.target.value, index });
            }}
          /> */}
        </div>
      </>
    );
  }
);

const AudioFileListRows = React.memo(
  ({ rows, deleteFile, group, flightingCodeChange, id, showRadio, selectedMp3Id, onSelectMp3Id, approvalStatus, qcStatus, currentQcVersion, currentFileVersion }) => {
    // Compute max QC version per file version (for determining which QC passed within each version)
    const maxQcPerVersion = {};
    rows.forEach(r => {
      const v = r.version || 1;
      const qv = r.qcVersion || 1;
      if (!maxQcPerVersion[v] || qv > maxQcPerVersion[v]) maxQcPerVersion[v] = qv;
    });
    return rows.map((row, index) => {
      return (
        <AudioFileRow
          key={`${id}-${index}`}
          data={{ ...row, currentFileVersion }}
          index={index}
          group={group}
          deleteFile={deleteFile}
          flightingCodeChange={flightingCodeChange}
          id={id}
          showRadio={showRadio}
          selectedMp3Id={selectedMp3Id}
          onSelectMp3Id={onSelectMp3Id}
          approvalStatus={approvalStatus}
          qcStatus={qcStatus}
          currentQcVersion={currentQcVersion}
          maxQcForVersion={maxQcPerVersion[row.version || 1] || 1}
        />
      );
    });
  }
);

const AudioFileList = React.memo(
  ({ files, deleteFileHandler, group, flightingCodeChange, id, showRadio, selectedMp3Id, onSelectMp3Id, approvalStatus, qcStatus, currentQcVersion, currentFileVersion }) => {
    if (files.length > 0) {
      return (
        <div className="audio-file-list">
          <AudioFileListRows
            key={`audio-rows-${id}`}
            rows={files}
            group={group}
            deleteFile={deleteFileHandler}
            flightingCodeChange={flightingCodeChange}
            id={id}
            showRadio={showRadio}
            selectedMp3Id={selectedMp3Id}
            onSelectMp3Id={onSelectMp3Id}
            approvalStatus={approvalStatus}
            qcStatus={qcStatus}
            currentQcVersion={currentQcVersion}
            currentFileVersion={currentFileVersion}
          />
        </div>
      );
    } else {
      return null;
    }
  }
);

export default AudioFileList;
